'use client';

import { useEffect, useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import { SkinEditorProvider, useSkinEditor } from './components/SkinEditorProvider';
import LayersPanel from './components/LayersPanel';
import { toolForShortcut } from '@/app/lib/skinEditor/tools';
import { useTrackToolView } from '@/app/hooks/useTrackToolView';

// Dynamic imports for components that use canvas/WebGL.
//
// ⚠️ Each one renders NOTHING on the server, so without a placeholder the page arrives ~56px tall and
// everything below (the guide) drops ~2,300px when they mount — field CLS was 0.76 on mobile
// (PageSpeed, 2026-09-19), the whole score attributed to section.tool-guide. Each placeholder is
// the MEASURED final height of that block at each breakpoint; change a component's layout → re-measure
// (server HTML vs hydrated, guide top must not move) and update the numbers here.
// ⚠️ Write a custom breakpoint in REM, not px: Tailwind v4 emits `min-[1690px]:` BEFORE its rem-based
// sm/md rules, so md:h-[118px] silently won at every width above it.
function Placeholder({ className }: { className: string }) {
  return <div aria-hidden="true" className={`bg-zinc-900 rounded-xl border border-zinc-800 ${className}`} />;
}
const Canvas2D = dynamic(() => import('./components/Canvas2D'), { ssr: false });
const Canvas2DSplit = dynamic(() => import('./components/Canvas2DSplit'), {
  ssr: false,
  loading: () => <Placeholder className="h-[850px]" />,
});
const Viewer3D = dynamic(() => import('./components/Viewer3D'), {
  ssr: false,
  loading: () => <Placeholder className="h-[956px] sm:h-[850px]" />,
});
const Toolbar = dynamic(() => import('./components/Toolbar'), {
  ssr: false,
  loading: () => <Placeholder className="h-[171px] max-[24.5rem]:h-[219px] sm:h-[123px] xl:h-[75px]" />,
});
const TopBar = dynamic(() => import('./components/TopBar'), {
  ssr: false,
  loading: () => <Placeholder className="h-[316px] max-[21.5rem]:h-[344px] sm:h-[166px] md:h-[118px] min-[105.625rem]:h-[66px]" />,
});

// Inner component that uses the context
function SkinEditorContent() {
  const {
    setTool,
    undoAction,
    redoAction,
    toggleMirror,
    toggleGrid,
    setBrushSize,
  } = useSkinEditor();

  const [editorView, setEditorView] = useState<'full' | 'split'>('split');

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't trigger shortcuts when typing in input fields
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }
    // …nor inside a dialog or popover (Import part, Publish, the Shading panel): "D" or Ctrl+Z there
    // must not switch tools or undo the skin behind it.
    if (e.target instanceof Element && e.target.closest('[role="dialog"]')) {
      return;
    }

    // Ctrl/Cmd + Z = Undo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undoAction();
      return;
    }

    // Ctrl/Cmd + Y or Ctrl/Cmd + Shift + Z = Redo
    if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
      e.preventDefault();
      redoAction();
      return;
    }

    // Ctrl/Cmd + G = Toggle Grid
    if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
      e.preventDefault();
      toggleGrid();
      return;
    }

    // Tool shortcuts (no modifiers other than Shift). Tool keys come from the tool registry
    // (app/lib/skinEditor/tools.ts): B, E, G, I, L, R, O, Shift+R, Shift+O, D, Shift+D, N.
    if (!e.ctrlKey && !e.metaKey && !e.altKey) {
      const tool = toolForShortcut(e.key, e.shiftKey);
      if (tool) {
        setTool(tool);
        return;
      }
      switch (e.key.toLowerCase()) {
        case 'm':
          toggleMirror();
          break;
        case '1':
          setBrushSize(1);
          break;
        case '2':
          setBrushSize(2);
          break;
        case '3':
          setBrushSize(3);
          break;
      }
    }
  }, [setTool, undoAction, redoAction, toggleMirror, toggleGrid, setBrushSize]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="p-4 pt-6">
      <div className="max-w-[1600px] mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-3">
            <svg className="w-7 h-7 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Minecraft Skin Editor
          </h1>
          {/* Rendered on the server (the editor itself is client-only), so this is the description of
              the tool a crawler sees. Keep it to features the editor really has. */}
          <p className="text-sm text-zinc-400 mt-1 ml-10 max-w-3xl">
            Paint a Minecraft skin in 2D or directly on the 3D model, in classic (Steve) or slim (Alex)
            arms, with layers. Load any player&apos;s skin by username or upload a PNG
            to edit it, use mirror mode to paint both sides at once, then download a 64x64 PNG ready for
            minecraft.net.
          </p>
        </div>

        {/* Top Bar - Import/Export & Model Type */}
        <div className="mb-4">
          <TopBar />
        </div>

        {/* Toolbar */}
        <div className="mb-4">
          <Toolbar />
        </div>

        {/* Main Content Grid - 3D Preview on left, 2D Editor on right. Two columns from 1440:
            single-column there stretched the 3D preview to ~1150px (arrows ~900px apart). */}
        <div className="grid grid-cols-1 min-[90rem]:grid-cols-12 gap-4">
          {/* Left Section - 3D Viewer with integrated controls */}
          <div className="min-[90rem]:col-span-5">
            <Viewer3D />
          </div>

          {/* Right Column - Layers + 2D Editor. The Layers panel is server-rendered with a fixed
              height (see LayersPanel), so it needs no loading placeholder and cannot shift the page. */}
          <div className="min-[90rem]:col-span-7">
            <div className="mb-3">
              <LayersPanel />
            </div>
            {/* View Toggle */}
            <div className="flex gap-1 mb-3">
              <button
                onClick={() => setEditorView('split')}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  editorView === 'split'
                    ? 'bg-purple-600 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
                }`}
              >
                Body Parts
              </button>
              <button
                onClick={() => setEditorView('full')}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  editorView === 'full'
                    ? 'bg-purple-600 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
                }`}
              >
                Full Texture
              </button>
            </div>

            {/* Editor Views */}
            {editorView === 'full' ? <Canvas2D /> : <Canvas2DSplit />}
          </div>
        </div>

      </div>
    </div>
  );
}

// Main page component with provider
export default function SkinEditorPage() {
  useTrackToolView('skin-editor');
  return (
    <SkinEditorProvider>
      <SkinEditorContent />
    </SkinEditorProvider>
  );
}
